import { Grid, makeStyles } from '@material-ui/core'
import ProfilePicture from 'components/common/ProfilePicture'
import React, { useMemo } from 'react'

import HoveredActions from 'components/common/HoveredActions'
import OpenInNewTabIcon from 'components/common/icons/OpenInNewTabIcon'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import Tooltip from 'components/common/Tooltip'
import PatientStatus from 'components/Patients/PatientStatus'
import { ROUTES } from 'consts'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { getEnvironmentHostname } from 'utils/awsUtils'
import useCopilotPatientsList from 'components/CopilotControl/hooks/useCopilotPatientsList'
import { trackEvent } from 'utils/analyticsUtils'
import { mapScoreToCategory } from 'components/CopilotControl/copilotLogic'

const SENTIMENT_LAYOUT_STYLES = {
  patientInfo: { width: '30%' },
  statusIcon: { width: '8%', textAlign: 'center' },
  lastComm: { width: '18%' },
  description: { width: '44%' }
}

const useStyles = makeStyles(() => ({
  patientItem: {
    position: 'relative',
    display: 'flex',
    padding: 20,
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E9'
  },
  avatar: {
    maxWidth: 80
  },
  patientNameStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 10,
    minWidth: 0,
    flex: 1
  },
  lastCommunicationLabel: {
    color: '#727B8C',
    paddingBottom: 5
  },
  noteContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: 20
  },
  sentimentDescription: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  centeredIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%'
  },
  statusIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
}))

const PatientsSentimentListItem = ({ patient, scoreOptions, index }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { getPatientStatusObject } = useCopilotPatientsList()

  const {
    id: patientId,
    name: patientName,
    profilePicture,
    statusKey,
    program,
    copilotChatSentimentDescription: sentimentDescription,
    copilotChatSentimentScore,
    lastMessageTimestamp
  } = patient

  const actions = useMemo(
    () =>
      [
        {
          key: 'openPatient',
          icon: <OpenInNewTabIcon />,
          tooltip: t('pages.copilotControl.widgets.needsAttention.actions.openPatient'),
          onClick: () => {
            trackEvent('Copilot Control - Patient opened from list', {
              view: 'Patient Sentiment',
              patientId
            })
            window.open(`${getEnvironmentHostname()}${ROUTES.PATIENTS}/${patientId}`, '_blank')
          },
          condition: () => true
        }
      ].filter(action => action.condition()),
    [patientId, t]
  )

  const patientStatus = getPatientStatusObject(statusKey, program)

  const itemCategory = mapScoreToCategory(copilotChatSentimentScore)
  const scoreOption = scoreOptions[itemCategory]
  const PatientCategoryIcon = scoreOptions[itemCategory].icon

  const [isHovered, setIsHovered] = React.useState(false)

  return (
    <Grid
      key={index}
      container
      className={classes.patientItem}
      alignItems="center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Grid item style={SENTIMENT_LAYOUT_STYLES.patientInfo}>
        <Grid container alignItems="center">
          <Grid item className={classes.avatar}>
            <ProfilePicture name={patientName} photo={profilePicture} />
          </Grid>
          <Grid item className={classes.patientNameStatusContainer}>
            <Grid item>
              <DazzedParagraph14 className={classes.sentimentDescription}>{patientName}</DazzedParagraph14>
            </Grid>
            <Grid item>
              <PatientStatus status={patientStatus} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={SENTIMENT_LAYOUT_STYLES.statusIcon} className={classes.statusIconContainer}>
        <div className={classes.centeredIcon}>
          <PatientCategoryIcon
            size="small"
            icon={scoreOption.icon}
            fillColor={scoreOption.fillColor}
            strokeColor={scoreOption.strokeColor}
            value={scoreOption.value || 0}
          />
        </div>
      </Grid>
      <Grid item style={SENTIMENT_LAYOUT_STYLES.lastComm}>
        <Grid container direction="column">
          <Grid item className={classes.lastCommunicationLabel}>
            Last Comm:
          </Grid>
          <Grid item>{moment(lastMessageTimestamp).format('MMM D, HH:mm')}</Grid>
        </Grid>
      </Grid>
      <Grid item className={classes.noteContainer} style={SENTIMENT_LAYOUT_STYLES.description}>
        <Tooltip value={sentimentDescription} placement="top" arrow variant="light">
          <div className={classes.sentimentDescription}>{sentimentDescription}</div>
        </Tooltip>
      </Grid>
      <HoveredActions actions={actions} isOpen={isHovered} placement="top" />
    </Grid>
  )
}

export default PatientsSentimentListItem
